import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, CalendarX2, RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/sections/Footer";

const API = process.env.REACT_APP_BACKEND_URL;
const KEY_STORAGE = "admin-key";

const fullFmt = new Intl.DateTimeFormat("el-GR", {
  weekday: "long",
  day: "numeric",
  month: "long",
});
const shortFmt = new Intl.DateTimeFormat("el-GR", { day: "2-digit", month: "2-digit" });

const STATUS_STYLES = {
  free: "border-border bg-card text-foreground/70 hover:border-foreground/50",
  blocked: "border-destructive/40 bg-destructive/10 text-destructive",
  pending: "border-amber-500/50 bg-amber-500/10 text-amber-700 cursor-default",
  approved: "border-primary/50 bg-primary/15 text-foreground cursor-default",
};

export default function Diaxeirisi() {
  const [key, setKey] = useState(localStorage.getItem(KEY_STORAGE) || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/schedule`, {
        headers: { "X-Admin-Key": k },
      });
      if (res.status === 401) {
        setError("Λάθος κωδικός διαχείρισης.");
        localStorage.removeItem(KEY_STORAGE);
        setKey("");
        setData(null);
      } else if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || "Προσωρινό σφάλμα — δοκίμασε ξανά σε λίγο.");
      } else {
        const d = await res.json();
        setData(d);
        localStorage.setItem(KEY_STORAGE, k);
        setKey(k);
      }
    } catch {
      setError("Σφάλμα σύνδεσης. Δοκίμασε ξανά.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (date, time) => {
    try {
      const res = await fetch(`${API}/api/admin/block`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Key": key },
        body: JSON.stringify({ date, time }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.status === 409) {
        toast.error(d.detail || "Υπάρχει ραντεβού σε αυτή την ώρα.");
        return;
      }
      if (!res.ok) throw new Error();
      toast.success(
        d.blocked
          ? time === "ALL" ? "Η μέρα μπλοκαρίστηκε." : `Η ώρα ${time} μπλοκαρίστηκε.`
          : time === "ALL" ? "Η μέρα ξεμπλοκαρίστηκε." : `Η ώρα ${time} είναι ξανά διαθέσιμη.`
      );
      load(key);
    } catch {
      toast.error("Κάτι πήγε στραβά.");
    }
  };

  const logout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setKey("");
    setData(null);
  };

  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>{"Διαχείριση · psyloveyourself"}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="bg-background pt-16 pb-24 min-h-screen" data-testid="admin-page">
        <div className="max-w-[880px] mx-auto px-6 sm:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-10"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Αρχική
          </Link>

          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-foreground">
            Διαχείριση <span className="italic font-light">διαθεσιμότητας</span>
          </h1>

          {!data ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-12 max-w-[420px] p-8 bg-card border border-border/60 rounded-3xl"
            >
              <p className="font-body text-sm text-foreground/80 flex items-center gap-2 mb-5">
                <Lock size={14} strokeWidth={1.5} className="text-primary" />
                Βάλε τον κωδικό διαχείρισης για να συνεχίσεις.
              </p>
              <input
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && input.trim() && load(input.trim())}
                placeholder="Κωδικός διαχείρισης"
                data-testid="admin-key-input"
                className="w-full rounded-xl bg-background border border-border px-4 py-3 font-body text-sm focus:outline-none focus:border-primary/60"
              />
              {error && (
                <p data-testid="admin-error" className="mt-3 font-body text-xs text-destructive">{error}</p>
              )}
              <button
                onClick={() => input.trim() && load(input.trim())}
                disabled={loading || !input.trim()}
                data-testid="admin-login-button"
                className="mt-5 w-full rounded-full bg-foreground text-background px-6 py-3 font-body text-sm font-medium disabled:opacity-50"
              >
                {loading ? "Έλεγχος…" : "Είσοδος"}
              </button>
            </motion.div>
          ) : (
            <div className="mt-10">
              <div className="flex items-center justify-between mb-8">
                <p className="font-body text-sm text-muted-foreground">
                  Πάτησε μια <strong className="text-foreground">ελεύθερη ώρα</strong> για να τη
                  μπλοκάρεις, ή μια <strong className="text-destructive">μπλοκαρισμένη</strong> για
                  να την ελευθερώσεις.
                </p>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => load(key)}
                    data-testid="admin-refresh"
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 font-body text-xs text-foreground/70 hover:text-foreground"
                  >
                    <RefreshCw size={12} strokeWidth={1.5} className={loading ? "animate-spin" : ""} />
                    Ανανέωση
                  </button>
                  <button
                    onClick={logout}
                    data-testid="admin-logout"
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 font-body text-xs text-foreground/70 hover:text-foreground"
                  >
                    <LogOut size={12} strokeWidth={1.5} />
                    Έξοδος
                  </button>
                </div>
              </div>

              {/* Upcoming bookings */}
              <section className="mb-12">
                <p className="font-body text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">
                  Επερχόμενα ραντεβού ({data.bookings.length})
                </p>
                {data.bookings.length === 0 ? (
                  <p className="font-body text-sm text-muted-foreground">Κανένα ραντεβού ακόμα.</p>
                ) : (
                  <div className="space-y-2" data-testid="admin-bookings-list">
                    {data.bookings.map((b) => (
                      <div
                        key={`${b.date}-${b.time}-${b.email}`}
                        className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-2xl border border-border/60 bg-card px-5 py-3.5 font-body text-sm"
                      >
                        <span className="font-medium text-foreground">
                          {shortFmt.format(new Date(b.date))} · {b.time}
                        </span>
                        <span className="text-foreground/80">{b.name}</span>
                        <span className="text-muted-foreground text-xs">
                          {b.type === "online" ? "Online" : "Δια ζώσης"} · {b.email}
                          {b.phone ? ` · ${b.phone}` : ""}
                        </span>
                        <span
                          className={`ml-auto rounded-full px-3 py-1 text-[11px] ${
                            b.status === "approved"
                              ? "bg-primary/15 text-foreground"
                              : "bg-amber-500/10 text-amber-700"
                          }`}
                        >
                          {b.status === "approved" ? "Εγκεκριμένο" : "Σε αναμονή"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Schedule grid */}
              <section className="space-y-6" data-testid="admin-schedule">
                {data.days.map((day) => (
                  <div key={day.date} className="rounded-2xl border border-border/50 bg-card/50 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-body text-sm font-medium text-foreground capitalize">
                        {fullFmt.format(new Date(day.date))}
                      </p>
                      <button
                        onClick={() => toggle(day.date, "ALL")}
                        data-testid="admin-block-day"
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-body text-[11px] transition-all ${
                          day.day_blocked
                            ? "border-destructive/40 bg-destructive/10 text-destructive"
                            : "border-border text-foreground/60 hover:border-foreground/40"
                        }`}
                      >
                        <CalendarX2 size={11} strokeWidth={1.5} />
                        {day.day_blocked ? "Ξεμπλόκαρε τη μέρα" : "Μπλόκαρε όλη τη μέρα"}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {day.slots.map((s) => (
                        <button
                          key={s.time}
                          onClick={() =>
                            (s.status === "free" || s.status === "blocked") &&
                            !day.day_blocked &&
                            toggle(day.date, s.time)
                          }
                          title={s.name ? `${s.name} (${s.status === "approved" ? "εγκεκριμένο" : "σε αναμονή"})` : ""}
                          data-testid="admin-slot"
                          data-status={s.status}
                          className={`rounded-full border px-3.5 py-1.5 font-body text-xs transition-all ${STATUS_STYLES[s.status]}`}
                        >
                          {s.time}
                          {s.name ? ` · ${s.name.split(" ")[0]}` : ""}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
